import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';

import type { BelegAntwort, BelegDetails } from '../api';
import { DruckErgebnisUnklar, beanspruecheAutoDruck, istGedruckt } from '../bondruck';
import { rechnungHtml, type RechnungUnternehmer } from '../rechnung-a4';

/**
 * Beleg wie gedruckt: Text vom Server (§ 132a BAO, § 11 RKSV) und der
 * maschinenlesbare Code als QR (Fehlerkorrektur M wie im BMF-Mustercode).
 */
export function Belegansicht(props: {
  beleg: BelegAntwort;
  text: string | null;
  aktionen?: React.ReactNode;
  /** Bon über die Bridge auf dem Bondrucker drucken; liefert eine Meldung (Fehler = Ablehnung). */
  bridgeDruck?: (belegnummer: string) => Promise<string>;
  /**
   * Gerade erstellter Beleg: einmal automatisch über die Bridge drucken (Einstellung der Kasse).
   * Jeder Beleg höchstens einmal – auch nach Neuladen nicht erneut.
   */
  autoDruck?: boolean;
  /** Beleg per E-Mail an eine Adresse senden; liefert eine Meldung. */
  emailSenden?: (belegnummer: string, an: string, form?: 'bon' | 'rechnung') => Promise<string>;
  /** Belegdetails nachladen – schaltet „Rechnung A4“ frei (Firmenkunden, § 11 UStG). */
  details?: () => Promise<BelegDetails>;
  unternehmer?: RechnungUnternehmer | null;
  /** Vorbelegung der E-Mail-Adresse (Kunde aus dem Kundenstamm). */
  emailVorschlag?: string | null;
}) {
  const leinwand = useRef<HTMLCanvasElement>(null);
  const { qrCode } = props.beleg;
  const [druckMeldung, setDruckMeldungRoh] = useState<string | null>(null);
  /** Ergebnis des Bridge-Drucks: true gedruckt, false Fehler (rot), null läuft/unklar. */
  const [druckOk, setDruckOk] = useState<boolean | null>(null);
  const [druckLaeuft, setDruckLaeuft] = useState(false);
  /** Meldung ohne Bewertung (E-Mail, Rechnung A4); der Bridge-Druck setzt druckOk selbst. */
  function setDruckMeldung(text: string | null): void {
    setDruckMeldungRoh(text);
    setDruckOk(null);
  }
  const bridgeDruck = props.bridgeDruck;
  const { kassenId, belegnummer } = props.beleg;
  const schonGedruckt = istGedruckt(localStorage, kassenId, belegnummer);

  function bridgeDrucken(): void {
    if (bridgeDruck === undefined) {
      return;
    }
    setDruckLaeuft(true);
    setDruckOk(null);
    setDruckMeldung('Bon wird gedruckt …');
    bridgeDruck(belegnummer)
      .then((m) => {
        // Erfüllt nur bei „gedruckt“/„übergeben“ (ok true); unklar und Fehler kommen als Ausnahme.
        setDruckMeldung(m);
        setDruckOk(true);
      })
      .catch((e: unknown) => {
        setDruckMeldung(e instanceof Error ? e.message : String(e));
        setDruckOk(e instanceof DruckErgebnisUnklar ? null : false);
      })
      .finally(() => {
        setDruckLaeuft(false);
      });
  }

  // Automatischer Bondruck nach dem Verkauf: genau einmal je Beleg (Merker auf dem Gerät).
  const autoDruck = props.autoDruck === true && bridgeDruck !== undefined;
  const autoGestartet = useRef<string | null>(null);
  useEffect(() => {
    if (!autoDruck || autoGestartet.current === belegnummer) {
      return;
    }
    autoGestartet.current = belegnummer;
    if (beanspruecheAutoDruck(localStorage, kassenId, belegnummer)) {
      bridgeDrucken();
    }
    // Nur beim ersten Anzeigen dieses Belegs.
  }, [autoDruck, kassenId, belegnummer]);
  const emailSenden = props.emailSenden;
  const [emailAn, setEmailAn] = useState(props.emailVorschlag ?? '');
  const [emailOffen, setEmailOffen] = useState(false);
  /** Statt des Bontexts die Rechnung im A4-Layout (HTML) senden – für Firmenkunden. */
  const [emailAlsRechnung, setEmailAlsRechnung] = useState(false);
  const details = props.details;

  /**
   * Rechnung A4 drucken: das Dokument wird in einen unsichtbaren iframe geladen
   * und von dort gedruckt – kein Pop-up, daher auch mit Pop-up-Blocker und auf
   * Tablets zuverlässig. Im Druckdialog kann als PDF gespeichert werden.
   */
  async function rechnungA4(): Promise<void> {
    if (details === undefined) {
      return;
    }
    try {
      const d = await details();
      // Der Server entscheidet mit allen Merkmalen (Betrag, Empfänger, UID, Kleinunternehmer).
      if (d.rechnung?.moeglich === false) {
        setDruckMeldung(d.rechnung.grund ?? 'Zu diesem Beleg ist keine Rechnung möglich');
        return;
      }
      const qr = await QRCode.toDataURL(d.qrCode, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 240,
      }).catch(() => null);
      const html = rechnungHtml(d, props.unternehmer ?? { name: '', anschrift: '' }, qr);
      const rahmen = document.createElement('iframe');
      rahmen.setAttribute('aria-hidden', 'true');
      rahmen.title = `Rechnung ${d.belegnummer}`;
      rahmen.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
      rahmen.addEventListener('load', () => {
        const w = rahmen.contentWindow;
        if (w === null) {
          rahmen.remove();
          setDruckMeldung('Rechnung A4 konnte nicht geladen werden');
          return;
        }
        w.addEventListener('afterprint', () => {
          rahmen.remove();
        });
        w.focus();
        w.print();
        // Falls kein afterprint kommt (ältere Browser), nach einer Minute aufräumen.
        setTimeout(() => {
          rahmen.remove();
        }, 60_000);
      });
      rahmen.srcdoc = html;
      document.body.appendChild(rahmen);
      setDruckMeldung(`Rechnung ${d.belegnummer} im A4-Format an den Drucker übergeben`);
    } catch (e: unknown) {
      setDruckMeldung(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    const c = leinwand.current;
    if (c === null) {
      return;
    }
    QRCode.toCanvas(c, qrCode, { errorCorrectionLevel: 'M', margin: 1, width: 220 }).catch(
      (e: unknown) => {
        console.error('QR-Code konnte nicht gezeichnet werden', e);
      },
    );
  }, [qrCode]);

  return (
    <div className="karte">
      <div className="beleg-druck">
        <pre className="bon">
          {props.text ?? `Beleg ${props.beleg.belegnummer} (${props.beleg.belegtyp})`}
        </pre>
        <div>
          <canvas ref={leinwand} width={220} height={220} aria-label="Maschinenlesbarer Code" />
          {props.beleg.signaturGestoert && (
            <p className="fehler">Sicherheitseinrichtung ausgefallen</p>
          )}
        </div>
      </div>
      <div className="zeile kein-druck">
        <button
          className="taste zweit"
          onClick={() => {
            window.print();
          }}
        >
          Drucken
        </button>
        {bridgeDruck !== undefined && (
          <button
            className="taste zweit"
            disabled={druckLaeuft}
            title={
              schonGedruckt
                ? 'Denselben Beleg nochmals drucken – mit Vermerk „KOPIE“, kein neuer Beleg'
                : 'Beleg auf dem Bondrucker dieser Kasse drucken'
            }
            onClick={bridgeDrucken}
          >
            {schonGedruckt ? 'Nachdruck (Kopie)' : 'Bon drucken'}
          </button>
        )}
        {emailSenden !== undefined && (
          <button
            className="taste zweit"
            onClick={() => {
              setEmailOffen(!emailOffen);
            }}
          >
            Per E-Mail
          </button>
        )}
        {details !== undefined && (
          <button
            className="taste zweit"
            title="Rechnung im A4-Format mit allen Rechnungsmerkmalen (Firmenkunden)"
            onClick={() => {
              void rechnungA4();
            }}
          >
            Rechnung A4
          </button>
        )}
        {props.aktionen}
      </div>
      {emailSenden !== undefined && emailOffen && (
        <div className="zeile kein-druck">
          <label>
            E-Mail-Adresse
            <input
              type="email"
              inputMode="email"
              autoFocus
              value={emailAn}
              onChange={(e) => {
                setEmailAn(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && emailAn.trim() !== '') {
                  emailSenden(
                    props.beleg.belegnummer,
                    emailAn.trim(),
                    emailAlsRechnung ? 'rechnung' : 'bon',
                  )
                    .then((m) => {
                      setDruckMeldung(m);
                      setEmailOffen(false);
                    })
                    .catch((err: unknown) => {
                      setDruckMeldung(err instanceof Error ? err.message : String(err));
                    });
                }
              }}
              style={{ minWidth: '16rem' }}
            />
          </label>
          <button
            className="taste"
            disabled={emailAn.trim() === ''}
            onClick={() => {
              emailSenden(
                props.beleg.belegnummer,
                emailAn.trim(),
                emailAlsRechnung ? 'rechnung' : 'bon',
              )
                .then((m) => {
                  setDruckMeldung(m);
                  setEmailOffen(false);
                })
                .catch((err: unknown) => {
                  setDruckMeldung(err instanceof Error ? err.message : String(err));
                });
            }}
          >
            Senden
          </button>
          {details !== undefined && (
            <label style={{ flexDirection: 'row', alignItems: 'center', gap: '0.3rem' }}>
              <input
                type="checkbox"
                checked={emailAlsRechnung}
                onChange={(e) => {
                  setEmailAlsRechnung(e.target.checked);
                }}
              />
              als Rechnung (A4-Layout)
            </label>
          )}
        </div>
      )}
      {druckMeldung !== null && (
        <p
          className={`${druckOk === false ? 'fehler' : druckOk === true ? 'ok' : 'hinweis'} kein-druck`}
          role="status"
        >
          {druckMeldung}
        </p>
      )}
    </div>
  );
}
